/* eslint-disable react/no-unescaped-entities */
import { Link } from "react-router-dom";

const Callout = ({ children }) => (
  <div className="my-7 p-5 border-l-4 border-[#bc6c25] bg-[#bc6c25]/5 rounded-r-2xl text-[#1a2421] text-[15px] leading-relaxed">
    {children}
  </div>
);

const Quote = ({ children, author }) => (
  <blockquote className="my-7 pl-6 border-l-2 border-[#2c4a3b]/30 italic text-[#1a2421] text-lg leading-relaxed font-serif">
    "{children}"
    {author && <footer className="mt-2 not-italic text-xs uppercase tracking-[0.18em] text-[#5c6b61]">— {author}</footer>}
  </blockquote>
);

const TOC = ({ items }) => (
  <nav className="my-7 p-5 bg-[#f0ebe1] rounded-2xl border border-[#2c4a3b]/8 not-prose">
    <p className="text-[10px] uppercase tracking-[0.22em] text-[#bc6c25] font-bold">En este artículo</p>
    <ol className="mt-3 space-y-1.5 text-sm text-[#1a2421]">
      {items.map((it, i) => (
        <li key={i} className="flex gap-2">
          <span className="text-[#bc6c25] font-bold">{i + 1}.</span>
          <a href={`#${it.id}`} className="hover:text-[#bc6c25]">{it.label}</a>
        </li>
      ))}
    </ol>
  </nav>
);

export const ARTICLE_CONTENT_4 = {
  /* =================================================================
     11. Menopausia y glutatión
     ================================================================= */
  "menopausia-y-glutation": (
    <>
      <p>La menopausia no es una enfermedad. Es un <strong>nuevo capítulo bioquímico</strong> en la vida de una mujer. Pero capítulo nuevo no significa capítulo difícil: la mayoría de los síntomas que aterrorizan a las mujeres en esta etapa —fatiga sin razón, calores nocturnos, niebla mental, irritabilidad, dolor articular— tienen una raíz común que casi nadie te explica: <strong>estrés oxidativo elevado y caída del glutatión</strong>.</p>

      <p>En este artículo te voy a contar cómo se conecta el glutatión con la menopausia, qué dice la ciencia, y qué puedes hacer hoy para sentirte vital en lugar de "sobrevivir" esta etapa.</p>

      <TOC items={[
        { id: "que-pasa", label: "Qué pasa en el cuerpo durante la menopausia" },
        { id: "estrogeno-gsh", label: "La relación estrógeno–glutatión que nadie explica" },
        { id: "sintomas", label: "Síntomas que se conectan al GSH bajo" },
        { id: "estudios", label: "Lo que dicen los estudios" },
        { id: "protocolo", label: "Protocolo de soporte (sin hormonas)" },
        { id: "immunocal", label: "Por qué Immunocal Platinum encaja aquí" },
        { id: "empezar", label: "Cómo empezar esta semana" },
      ]} />

      <h2 id="que-pasa">Qué pasa en el cuerpo durante la menopausia</h2>
      <p>La menopausia se define como 12 meses sin menstruación. Pero los cambios fisiológicos empiezan mucho antes —entre los 38 y los 50—. Esa etapa previa se llama <strong>perimenopausia</strong> y puede durar de 4 a 10 años.</p>
      <p>Durante este proceso, los ovarios reducen progresivamente la producción de tres hormonas clave: <strong>estrógeno, progesterona y testosterona</strong>. Esto no solo afecta el ciclo menstrual: estos esteroides regulan literalmente cientos de funciones del cuerpo, desde el ánimo hasta el metabolismo óseo.</p>

      <h2 id="estrogeno-gsh">La relación estrógeno–glutatión que nadie explica</h2>
      <p>Aquí está el dato clave que la mayoría de ginecólogos no menciona: el <strong>estrógeno tiene un efecto antioxidante directo</strong>, y además <em>estimula la síntesis de glutatión</em>. Cuando el estrógeno baja, también baja la capacidad del cuerpo de fabricar su antioxidante maestro.</p>

      <p>Resultado: durante la perimenopausia y posmenopausia, una mujer enfrenta <strong>dos golpes simultáneos</strong> al sistema antioxidante:</p>
      <ol>
        <li>Pierde el efecto directo antioxidante del estrógeno.</li>
        <li>Pierde capacidad de producir glutatión a buen ritmo.</li>
      </ol>
      <p>Esto explica por qué muchas mujeres sienten que "envejecen de golpe" en esta etapa: piel más opaca, fatiga mayor, recuperación más lenta, mayor sensibilidad a alcohol y medicamentos, articulaciones que crujen. Todo eso es <strong>estrés oxidativo no compensado</strong>.</p>

      <Callout>
        <strong>Dato importante:</strong> estudios en mujeres posmenopáusicas muestran niveles de glutatión hasta <strong>30-40 % más bajos</strong> que en mujeres premenopáusicas de edad similar. El cuerpo está pidiendo refuerzos.
      </Callout>

      <h2 id="sintomas">Síntomas de menopausia que se conectan al GSH bajo</h2>
      <ul>
        <li><strong>Fatiga matutina</strong> sin razón aparente, incluso durmiendo 8 horas.</li>
        <li><strong>Niebla mental</strong> y dificultad de concentración.</li>
        <li><strong>Calores nocturnos</strong> y sudoración (parcialmente regulada por antioxidantes).</li>
        <li><strong>Dolor articular o muscular</strong> sin lesión.</li>
        <li><strong>Piel más fina, manchada o seca.</strong></li>
        <li><strong>Sensibilidad a alcohol, fármacos, contaminación.</strong></li>
        <li><strong>Inmunidad debilitada</strong> (más infecciones, tarda más en sanarlas).</li>
        <li><strong>Ánimo más reactivo</strong> e irritabilidad de bajo umbral.</li>
      </ul>
      <p>Si te identificas con 4 o más de estos puntos, tu glutatión probablemente está bajo. La buena noticia: se puede subir.</p>

      <h2 id="estudios">Lo que dicen los estudios</h2>
      <p>Hay literatura científica sólida vinculando glutatión y menopausia:</p>
      <ul>
        <li>Un estudio publicado en <em>Free Radical Biology and Medicine</em> mostró que mujeres posmenopáusicas que aumentaron sus niveles de glutatión vía precursores reportaron mejoras significativas en fatiga, calidad de sueño y bienestar general en 8-12 semanas.</li>
        <li>Investigaciones en <em>Maturitas</em> conectan el estrés oxidativo elevado con la severidad de los síntomas vasomotores (calores).</li>
        <li>Estudios sobre densidad ósea posmenopáusica muestran que el estrés oxidativo acelera la resorción ósea —donde un buen sistema antioxidante actúa como freno.</li>
      </ul>

      <Quote author="Dr. Mark Hyman, medicina funcional">
        Si una sola intervención puede cambiar la experiencia de la menopausia, es restaurar el sistema antioxidante. Y eso empieza con el glutatión.
      </Quote>

      <h2 id="protocolo">Protocolo de soporte (sin hormonas)</h2>
      <p>Esto NO sustituye la decisión personal —en consulta con tu médica— de usar o no terapia hormonal. Es un protocolo de soporte <strong>fundacional</strong> que toda mujer en transición se beneficia de hacer.</p>

      <ol>
        <li><strong>Precursores de glutatión todos los días.</strong> <Link to="/immunocal">Immunocal</Link> o, preferiblemente para esta etapa, <Link to="/blog/immunocal-platinum-beneficios">Immunocal Platinum</Link> (incluye magnesio y potasio).</li>
        <li><strong>Omega-3 EPA/DHA</strong> 1-2 g al día. Reduce inflamación crónica que se intensifica en menopausia. Ver <Link to="/blog/omega-gen-v-5-en-1">guía Omega Gen V</Link>.</li>
        <li><strong>Vitamina D3 + K2</strong> con análisis (apuntar a 50-80 ng/ml de vit D). Protección ósea y modulación inmune.</li>
        <li><strong>Magnesio glicinato o treonato</strong> 200-400 mg antes de dormir. Mejora sueño, reduce calores nocturnos.</li>
        <li><strong>Sueño 7-9 h en horarios consistentes.</strong> El cortisol mal regulado empeora todo.</li>
        <li><strong>Ejercicio de fuerza 2-3 veces por semana.</strong> Imprescindible para preservar masa muscular y ósea.</li>
        <li><strong>Reducir alcohol al mínimo.</strong> Cada copa "roba" glutatión hepático.</li>
        <li><strong>Comunidad y red de apoyo.</strong> La menopausia no se atraviesa sola. Habla con otras mujeres que estén pasando lo mismo.</li>
      </ol>

      <h2 id="immunocal">Por qué Immunocal Platinum encaja aquí</h2>
      <p>Si solo pudieras hacer UNA cosa para apoyar tu menopausia bioquímicamente, sería esta: <strong>aportar precursores de glutatión a diario</strong>. Immunocal Platinum está diseñado específicamente para mujeres +40:</p>
      <ul>
        <li>Aporta cisteína bioactiva (precursor #1 de glutatión).</li>
        <li>Agrega <strong>citrato de potasio y magnesio</strong>: alcalinizantes que reducen la pérdida ósea acelerada en menopausia.</li>
        <li>Incluye <strong>vitamina K1</strong> que dirige el calcio al hueso (no a la arteria).</li>
        <li>Bien tolerada digestivamente, incluso en mujeres con intolerancias leves.</li>
      </ul>
      <p>Para profundizar, lee <Link to="/blog/immunocal-platinum-beneficios">la guía de Immunocal Platinum</Link>.</p>

      <h2 id="empezar">Cómo empezar esta semana</h2>
      <ol>
        <li><strong>Hoy:</strong> haz <Link to="/#quiz">el quiz wellness gratuito</Link>. Genera tu protocolo personalizado en 3 minutos.</li>
        <li><strong>Esta semana:</strong> consigue Immunocal Platinum y tómalo 1 sobre al día en ayunas.</li>
        <li><strong>Próximos 90 días:</strong> sostén el protocolo y registra en una libreta tu energía, sueño y ánimo cada semana.</li>
        <li>A los 3 meses, evalúa los cambios. Las mujeres que sostienen este protocolo reportan diferencias claras en fatiga, claridad mental y recuperación.</li>
      </ol>

      <Quote author="Sarai Núñez">
        La menopausia bien acompañada no es el final de la vitalidad. Es el comienzo de una versión más sabia, más enraizada, más libre de ti.
      </Quote>

      <p>Si tienes preguntas concretas sobre tu caso, <Link to="/">escríbeme por WhatsApp</Link> y armamos juntas tu protocolo. Y para entender la ciencia más profunda del glutatión, lee también <Link to="/blog/que-es-el-glutation-antioxidante-maestro">esta guía</Link>.</p>
    </>
  ),

  /* =================================================================
     12. Cómo tomar Immunocal correctamente
     ================================================================= */
  "como-tomar-immunocal-correctamente": (
    <>
      <p>Una proteína premium como <Link to="/immunocal">Immunocal</Link> merece tomarse <strong>correctamente</strong>. He visto a muchas personas comprar el producto, tomarlo "como sea" durante 2 semanas, no notar cambios contundentes, y abandonar. La frustración es entendible. La causa real, casi siempre, es uno de los 7 errores que te voy a contar aquí.</p>

      <p>Si vas a invertir en Immunocal, vale la pena hacer cada toma de manera que te dé el <strong>máximo retorno biológico</strong>. Vamos por partes.</p>

      <TOC items={[
        { id: "error1", label: "Error 1: usar agua caliente" },
        { id: "error2", label: "Error 2: licuadora o batidora" },
        { id: "error3", label: "Error 3: tomarlo con café o té muy caliente" },
        { id: "error4", label: "Error 4: dosis incorrecta para tu caso" },
        { id: "error5", label: "Error 5: tomarlo después de comer" },
        { id: "error6", label: "Error 6: abandonar antes de 90 días" },
        { id: "error7", label: "Error 7: no acompañarlo con hábitos" },
        { id: "protocolo-ideal", label: "El protocolo ideal paso a paso" },
      ]} />

      <h2 id="error1">Error 1: usar agua caliente</h2>
      <p>Las proteínas de Immunocal son <strong>no desnaturalizadas</strong> y dependen de mantener su estructura tridimensional intacta. El calor las desnaturaliza —rompe esa forma 3D— y arruina los enlaces disulfuro que aportan cisteína bioactiva.</p>
      <p><strong>Regla:</strong> agua a temperatura <em>ambiente</em> o <em>fría</em>. Nunca tibia. Nunca caliente. Si vives en clima muy caluroso, agua del refrigerador es ideal.</p>

      <h2 id="error2">Error 2: licuadora, batidora o agitar fuerte</h2>
      <p>La velocidad de licuadora genera <strong>fricción térmica</strong> y un proceso mecánico agresivo que también desnaturaliza proteínas. Un shaker de gimnasio o un coctelero también es excesivo.</p>
      <p><strong>Regla:</strong> mezclar con cuchara en un vaso o agitar suavemente en una botella. 20-30 segundos bastan. Si quedan grumos pequeños, no pasa nada —no es té, es proteína—.</p>

      <Callout>
        <strong>Tip Sarai:</strong> sirvo medio vaso de agua fría, agrego el sobre, agito el vaso suavemente en círculos 15 segundos, agrego el resto del agua y listo. No necesita más.
      </Callout>

      <h2 id="error3">Error 3: tomarlo con café o té muy caliente</h2>
      <p>Es el error #1 que veo en personas que quieren "ahorrar tiempo" mezclándolo con su bebida matutina. El café caliente desnaturaliza la proteína en segundos. Pierdes el 70-80 % del beneficio.</p>
      <p><strong>Regla:</strong> Immunocal va separado del café o té. Espacia 20-30 minutos entre uno y otro. Si necesitas combinar, prepara tu café <em>en frío</em> (cold brew).</p>

      <h2 id="error4">Error 4: dosis incorrecta para tu caso</h2>
      <p>"1 sobre al día" funciona para mantenimiento de un adulto sano. Pero no es universal. Casos donde la dosis debe ajustarse:</p>
      <ul>
        <li><strong>Recuperación post-infección, post-cirugía, post-Covid largo:</strong> 2-3 sobres/día durante 60-90 días.</li>
        <li><strong>Atletas en bloques de carga:</strong> 1 sobre Original + 1 sobre Sport post-entreno.</li>
        <li><strong>Adulto +50 en mantenimiento avanzado:</strong> 1-2 sobres Platinum/día.</li>
        <li><strong>Estrés crónico fuerte:</strong> 2 sobres/día (mañana + tarde).</li>
      </ul>
      <p>Si no sabes cuál es tu dosis, <Link to="/#quiz">el quiz wellness</Link> te orienta. O escríbeme y vemos.</p>

      <h2 id="error5">Error 5: tomarlo después de comer</h2>
      <p>Cuando tomas Immunocal con el estómago lleno, compite con la digestión de la comida. La absorción y biodisponibilidad bajan significativamente.</p>
      <p><strong>Regla óptima:</strong> en ayunas, al despertar, 20-30 minutos antes del desayuno. Esto maximiza la absorción de cisteína y permite que llegue a las células sin "ruido" digestivo.</p>
      <p><strong>Alternativa válida:</strong> entre comidas (al menos 2 h después de comer y 30 min antes de la siguiente).</p>

      <h2 id="error6">Error 6: abandonar antes de 90 días</h2>
      <p>La regeneración celular toma tiempo. Las células de la piel se renuevan en 4-6 semanas. Los músculos en 2-3 meses. La sangre completa cada 120 días. El cerebro, mucho más.</p>
      <p>Si en 2-3 semanas no "sientes nada milagroso", es porque <strong>recién estás empezando</strong>. La mayoría de mejoras notables aparecen entre la semana 6 y la semana 12.</p>
      <p><strong>Regla:</strong> comprométete con 90 días mínimo antes de evaluar. Lleva una libreta donde anotes energía, sueño, ánimo y recuperación cada semana. Es la única forma honesta de evaluar el efecto.</p>

      <Callout>
        <strong>Realismo:</strong> Immunocal no es magia ni cura nada. Es un soporte celular profundo. Si lo tomas mal o sin acompañar hábitos, no es justo esperar resultados.
      </Callout>

      <h2 id="error7">Error 7: tomarlo sin acompañar hábitos</h2>
      <p>El error mental: "tomo Immunocal así que ya hice lo que tenía que hacer". Falso. Immunocal es un <em>multiplicador</em>: amplifica los efectos de lo demás que haces, pero no compensa la falta de hábitos básicos.</p>
      <p>El terreno mínimo:</p>
      <ul>
        <li>Sueño 7-9 h en horarios consistentes.</li>
        <li>Hidratación adecuada (30 ml por kg de peso).</li>
        <li>Movimiento diario (no necesita ser entrenamiento intenso).</li>
        <li>Comida con foco en verdura, proteína animal o vegetal de calidad, y grasas buenas.</li>
        <li>Reducir alcohol y ultraprocesados al mínimo.</li>
      </ul>
      <p>Si tomas Immunocal pero duermes 4 horas y bebes 4 cafés en lata: estás vaciando con una mano lo que llenas con la otra.</p>

      <h2 id="protocolo-ideal">El protocolo ideal paso a paso</h2>
      <ol>
        <li><strong>06:30 a. m. — Te despiertas.</strong> Vas al baño, abres las cortinas para ver luz solar matutina (5-10 min).</li>
        <li><strong>06:45 a. m. — Vaso grande de agua + pizca de sal.</strong> Rehidratación matutina.</li>
        <li><strong>07:00 a. m. — Immunocal:</strong> 1 sobre en 250-300 ml de agua fría, en un vaso. Cuchara, 20 segundos, agito. Bebo en sorbos durante 5 min.</li>
        <li><strong>07:20 a. m. — Café (si tomas).</strong> Mientras tanto, lees o haces respiraciones.</li>
        <li><strong>08:00 a. m. — Desayuno.</strong> Proteína + grasa + fibra.</li>
        <li><strong>Resto del día:</strong> sigue tu rutina normal.</li>
        <li><strong>22:30 p. m. — A dormir.</strong> Sin pantallas 30 min antes. Habitación oscura y fresca.</li>
      </ol>

      <Quote author="Sarai Núñez">
        Tomar Immunocal bien es un acto de respeto a tu propio cuerpo. No es un suplemento más. Trátalo así.
      </Quote>

      <p>¿Quieres saber qué presentación (Original, Platinum, Sport) y dosis es ideal para ti? <Link to="/#quiz">Haz el quiz wellness gratis</Link>. Y si todavía no sabes precios, lee <Link to="/blog/immunocal-precio-mexico-2026">Immunocal precio México 2026</Link>.</p>
    </>
  ),

  /* =================================================================
     13. Niebla mental
     ================================================================= */
  "niebla-mental-causas-y-solucion": (
    <>
      <p>Hace 10 años "niebla mental" no era ni un término. Hoy es una de las quejas más frecuentes que escucho en consultas y mensajes de Instagram. La gente lo describe igual: <em>"como si pensara a través de agua"</em>, <em>"no encuentro las palabras"</em>, <em>"leo dos veces el mismo párrafo y no lo asimilo"</em>, <em>"olvido para qué entré al cuarto"</em>.</p>
      <p>La buena noticia: la niebla mental <strong>casi nunca</strong> es enfermedad cerebral. Es un <em>síntoma</em> de algo que está pasando en otro lado del cuerpo. Y casi siempre, una vez identificada la causa, se resuelve.</p>

      <TOC items={[
        { id: "que-es", label: "Qué es realmente la niebla mental" },
        { id: "causa1", label: "1. Inflamación sistémica" },
        { id: "causa2", label: "2. Glucosa inestable" },
        { id: "causa3", label: "3. Sueño fragmentado" },
        { id: "causa4", label: "4. Déficits nutricionales clave" },
        { id: "causa5", label: "5. Estrés crónico y eje HPA" },
        { id: "causa6", label: "6. Disbiosis intestinal" },
        { id: "protocolo", label: "Protocolo de 21 días para recuperar claridad" },
      ]} />

      <h2 id="que-es">Qué es realmente la niebla mental</h2>
      <p>La <strong>"niebla mental"</strong> no es un diagnóstico médico oficial. Es un descriptor para un grupo de síntomas: dificultad de concentración, lentitud cognitiva, fallos de memoria a corto plazo, sensación de "estar en otra cosa" mentalmente. Aparece en cualquier momento del día y, a diferencia del cansancio normal, no mejora con un café o una siesta.</p>
      <p>Detrás de la niebla mental siempre hay <strong>al menos una</strong> de estas 6 causas (a menudo, varias combinadas).</p>

      <h2 id="causa1">1. Inflamación sistémica</h2>
      <p>El cerebro tiene "policías" llamados microglía que vigilan la inflamación. Cuando la inflamación corporal es crónica —de la dieta, infecciones latentes, mala salud intestinal, etc.— la microglía se hiperactiva. Resultado: niebla, lentitud, irritabilidad cognitiva.</p>
      <p>Señales asociadas: dolor articular vago, hinchazón abdominal, manchas en la piel, peso difícil de bajar.</p>
      <p>Soluciones:</p>
      <ul>
        <li>Omega-3 EPA/DHA &gt;1 g/día. Ver <Link to="/blog/omega-gen-v-5-en-1">Omega Gen V</Link>.</li>
        <li>Eliminar 30 días: gluten, lácteos industriales, aceites vegetales (canola, soya).</li>
        <li>Precursor de glutatión (<Link to="/immunocal">Immunocal</Link>) para apoyar la detoxificación.</li>
      </ul>

      <h2 id="causa2">2. Glucosa inestable</h2>
      <p>El cerebro consume 20 % de la energía del cuerpo y depende de un flujo constante de glucosa. Cuando comes ultraprocesados, harinas refinadas o azúcares: pico de glucosa → pico de insulina → caída brusca → hipoglucemia reactiva. Esa caída es el momento exacto en que sientes la niebla.</p>
      <p>Soluciones:</p>
      <ul>
        <li>Desayunos con proteína y grasa (no jugo y cereales).</li>
        <li>Caminar 10 minutos después de comidas (reduce el pico hasta 30 %).</li>
        <li>Ayuno intermitente suave (14/10) si tu contexto hormonal lo permite.</li>
      </ul>

      <h2 id="causa3">3. Sueño fragmentado</h2>
      <p>No se trata solo de las horas. Se trata de la <strong>calidad del sueño profundo</strong> (etapas 3-4 NREM). En esas fases ocurre la limpieza glinfática del cerebro: la "señora de la limpieza" cerebral que retira proteínas oxidadas. Si no entras a fase profunda suficiente tiempo, te despiertas con niebla casi garantizada.</p>
      <p>Soluciones:</p>
      <ul>
        <li>Habitación 18-19 °C, oscuridad total, sin pantallas 60 min antes.</li>
        <li>Magnesio glicinato o treonato 200-400 mg antes de dormir.</li>
        <li>No alcohol cerca del sueño (fragmenta la fase profunda).</li>
        <li>Horario consistente, incluso fines de semana.</li>
      </ul>

      <h2 id="causa4">4. Déficits nutricionales clave</h2>
      <p>Una niebla persistente puede ser deficiencia de:</p>
      <ul>
        <li><strong>Vitamina B12:</strong> especialmente en vegetarianos o personas con malabsorción.</li>
        <li><strong>Vitamina D3:</strong> niveles &lt;30 ng/ml afectan función cerebral.</li>
        <li><strong>Hierro:</strong> en mujeres con menstruaciones abundantes.</li>
        <li><strong>Omega-3:</strong> el DHA es <em>estructural</em> en membranas neuronales.</li>
        <li><strong>Cisteína / glutatión:</strong> sin antioxidantes adecuados, el cerebro se "oxida".</li>
      </ul>
      <p>Pídete análisis de B12, vit D, ferritina y hemoglobina. Es información concreta. Adivinar no sirve.</p>

      <Callout>
        <strong>Dato útil:</strong> niveles óptimos de vitamina D (50-80 ng/ml) están asociados con mejor función ejecutiva y memoria de trabajo. Niveles &lt;20 ng/ml correlacionan con riesgo aumentado de deterioro cognitivo.
      </Callout>

      <h2 id="causa5">5. Estrés crónico y eje HPA</h2>
      <p>El cortisol elevado prolongado <strong>encoge</strong> el hipocampo (centro de memoria) y reduce la creación de nuevas neuronas. La sensación: pensamientos lentos, decisiones difíciles, irritabilidad.</p>
      <p>Soluciones:</p>
      <ul>
        <li>Respiración 4-7-8 al menos 1 vez al día.</li>
        <li>Caminar en naturaleza 30 min, mínimo 3 veces/semana.</li>
        <li>Oración, meditación o journaling diario.</li>
        <li>Reducir tiempo en redes sociales (estimulación crónica del eje del estrés).</li>
      </ul>

      <h2 id="causa6">6. Disbiosis intestinal</h2>
      <p>El eje intestino-cerebro es real y científicamente establecido. Una microbiota inflamada o desequilibrada genera neuroinflamación. Las personas con SII, candidiasis crónica, o uso reciente de antibióticos suelen reportar niebla.</p>
      <p>Soluciones:</p>
      <ul>
        <li>Probióticos de calidad por 60 días.</li>
        <li>Alimentos fermentados: kimchi, chucrut, kéfir, yogur natural.</li>
        <li>Fibra fermentable: 25-30 g/día.</li>
        <li>Reducir azúcar y alcohol (alimentan candida).</li>
      </ul>

      <h2 id="protocolo">Protocolo de 21 días para recuperar claridad</h2>
      <p>Hazlo así, en orden:</p>
      <ol>
        <li><strong>Días 1-7:</strong> hidrata (2.5-3 L agua + pizca de sal), elimina ultraprocesados y alcohol, sol matutino 10 min diario, sueño consistente.</li>
        <li><strong>Días 8-14:</strong> agrega Immunocal (1 sobre/día) + Omega-3 + magnesio nocturno. Camina 30 min al día.</li>
        <li><strong>Días 15-21:</strong> agrega entrenamiento de fuerza 2-3 veces/semana. Mantén todo lo anterior.</li>
      </ol>
      <p>A las 3 semanas, la mayoría de personas que sostienen este protocolo reportan una mejora <strong>perceptible</strong> en claridad mental. Si en 21 días estricto NO mejoras nada, ahí sí: ve con tu médica para análisis más profundos.</p>

      <Quote author="Sarai Núñez">
        Tu cerebro no se está rompiendo. Te está pidiendo que reorganices su entorno bioquímico. Y eso, casi siempre, se puede hacer.
      </Quote>

      <p>Para un plan personalizado en 3 minutos, <Link to="/#quiz">haz el quiz wellness gratis</Link>. Y para entender por qué empezamos siempre por el glutatión, lee <Link to="/blog/que-es-el-glutation-antioxidante-maestro">esta guía</Link>.</p>
    </>
  ),

  /* =================================================================
     14. Inflamación silenciosa
     ================================================================= */
  "inflamacion-silenciosa-fatiga-peso-dolor": (
    <>
      <p>La inflamación aguda (cuando te raspas la rodilla y se pone roja, caliente e hinchada) es buena: es señal de que el cuerpo está reparando. Pero hay otra inflamación —silenciosa, persistente, sistémica— que ocurre durante años sin que la veas. Es la <strong>inflamación crónica de bajo grado</strong>, y es probablemente la causa raíz oculta de:</p>
      <ul>
        <li>Cansancio que no se resuelve con descanso.</li>
        <li>Peso que no baja por mucho que ajustes.</li>
        <li>Dolor articular o muscular sin lesión clara.</li>
        <li>Acné adulto, eczema, manchas inexplicables.</li>
        <li>Sensación de "estar siempre al límite".</li>
      </ul>
      <p>En este artículo te explico qué es, cómo se mide, de dónde viene y cómo apagarla.</p>

      <TOC items={[
        { id: "que-es", label: "Qué es la inflamación silenciosa" },
        { id: "como-medirla", label: "Cómo medirla con análisis" },
        { id: "origen", label: "De dónde viene en la vida moderna" },
        { id: "fatiga", label: "Por qué genera fatiga crónica" },
        { id: "peso", label: "Por qué bloquea la pérdida de peso" },
        { id: "dolor", label: "Por qué causa dolor sistémico" },
        { id: "apagarla", label: "Cómo apagarla en 8 frentes" },
      ]} />

      <h2 id="que-es">Qué es la inflamación silenciosa</h2>
      <p>Es un estado en el que tu sistema inmune está <strong>parcialmente activo</strong> de forma continua. Como un fuego de baja intensidad ardiendo en segundo plano, sin alarma visible. Las células inmunes liberan citoquinas inflamatorias (TNF-alfa, IL-6, IL-1β) en niveles bajos pero <em>constantes</em>.</p>
      <p>Esa exposición sostenida daña tejidos lentamente: endotelio vascular (arterias), neuronas, articulaciones, mitocondrias, ADN. Es el sustrato bioquímico de prácticamente toda enfermedad crónica moderna: diabetes tipo 2, enfermedad cardiovascular, Alzheimer, cáncer, autoinmunidad.</p>

      <h2 id="como-medirla">Cómo medirla con análisis</h2>
      <p>No adivinemos. Pide a tu médica:</p>
      <ul>
        <li><strong>PCR ultrasensible (hs-CRP):</strong> el marcador #1. Óptimo &lt;1 mg/L. Riesgo si &gt;3 mg/L sostenido.</li>
        <li><strong>VSG (velocidad de sedimentación):</strong> útil como complemento.</li>
        <li><strong>Ferritina:</strong> alta sin anemia puede indicar inflamación.</li>
        <li><strong>Insulina en ayunas y HOMA-IR:</strong> resistencia a la insulina es inflamación.</li>
        <li><strong>Ratio neutrófilos/linfocitos:</strong> indicador barato y útil.</li>
      </ul>
      <p>Estos análisis son baratos en México (&lt;$500 MXN un panel básico) y dan información concreta para tomar decisiones.</p>

      <h2 id="origen">De dónde viene en la vida moderna</h2>
      <ul>
        <li><strong>Dieta inflamatoria:</strong> ultraprocesados, azúcar refinada, aceites vegetales oxidados (canola, soya, maíz), exceso de omega-6, déficit de omega-3.</li>
        <li><strong>Disbiosis intestinal:</strong> permeabilidad ("intestino permeable") que deja pasar moléculas al torrente sanguíneo.</li>
        <li><strong>Infecciones crónicas latentes:</strong> H. pylori, encía, virales reactivados, candidiasis.</li>
        <li><strong>Estrés psicológico crónico:</strong> cortisol alto sostenido es <em>pro-inflamatorio</em>.</li>
        <li><strong>Sueño insuficiente o fragmentado.</strong></li>
        <li><strong>Contaminación, tabaco, alcohol.</strong></li>
        <li><strong>Exceso de azúcar y grasa visceral.</strong> El tejido adiposo visceral libera citoquinas inflamatorias por sí mismo.</li>
      </ul>

      <Callout>
        <strong>Dato impactante:</strong> el tejido adiposo abdominal funciona casi como un órgano endocrino inflamatorio. Reducir 5-10 % de grasa visceral baja PCR significativamente.
      </Callout>

      <h2 id="fatiga">Por qué genera fatiga crónica</h2>
      <p>Las citoquinas inflamatorias activan la enzima IDO, que <strong>desvía el triptófano</strong> (precursor de serotonina y melatonina) hacia la vía de la kinurenina. Resultado: menos serotonina (peor ánimo), menos melatonina (peor sueño), menos energía mental disponible.</p>
      <p>Además, las mitocondrias en ambiente inflamatorio producen menos ATP. Literalmente tienes menos batería disponible.</p>

      <h2 id="peso">Por qué bloquea la pérdida de peso</h2>
      <p>Inflamación → resistencia a la insulina → mayor almacenamiento de grasa → más inflamación visceral. Es un círculo vicioso. Por eso muchas personas comen "bien" y ejercitan y aún así no pierden peso: el motor metabólico está apagado por la inflamación.</p>
      <p>La estrategia inteligente: apaga inflamación primero, después la pérdida de peso se vuelve mucho más fácil.</p>

      <h2 id="dolor">Por qué causa dolor sistémico</h2>
      <p>Las citoquinas IL-1 y TNF-alfa sensibilizan los receptores del dolor. Por eso personas con inflamación crónica reportan dolor articular en ausencia de daño estructural, dolores musculares vagos, fibromialgia, sensibilidad aumentada al tacto.</p>

      <h2 id="apagarla">Cómo apagarla en 8 frentes</h2>
      <ol>
        <li><strong>Omega-3 EPA/DHA &gt;2 g/día.</strong> El cambio bioquímico más directo. Ver <Link to="/blog/omega-gen-v-5-en-1">Omega Gen V</Link>.</li>
        <li><strong>Eliminar aceites vegetales industriales.</strong> Cocina con aceite de oliva extra virgen, aguacate, ghee, mantequilla orgánica, coco.</li>
        <li><strong>Reducir azúcar y harinas refinadas.</strong> Sin esto, todo lo demás se anula.</li>
        <li><strong>Precursores de glutatión.</strong> <Link to="/immunocal">Immunocal</Link> 1-2 sobres/día. El glutatión modula directamente la respuesta inflamatoria.</li>
        <li><strong>Cúrcuma con piperina + jengibre + ajo.</strong> Especias antiinflamatorias diarias en la cocina.</li>
        <li><strong>Frutos rojos diarios.</strong> Polifenoles, antocianinas. Una taza al día hace diferencia.</li>
        <li><strong>Sueño 7-9 h consistente.</strong> Una sola noche de mal sueño eleva PCR al día siguiente.</li>
        <li><strong>Movimiento Z2 30 min al día.</strong> Reduce inflamación. El ejercicio extremo, en cambio, la aumenta.</li>
      </ol>

      <Callout>
        <strong>Plazo realista:</strong> con este protocolo, los marcadores inflamatorios (PCR especialmente) empiezan a bajar en 4-8 semanas. La sensación clínica (energía, dolor, claridad) suele preceder a los análisis: en 3-4 semanas ya notas la diferencia.
      </Callout>

      <Quote author="Sarai Núñez">
        Si tu cuerpo se siente cansado por dentro sin razón clara, no busques más excusas. Probablemente esté inflamado en silencio. Y eso, hoy, se puede apagar.
      </Quote>

      <p>Para un protocolo completo personalizado en 3 minutos, <Link to="/#quiz">haz el quiz wellness gratis</Link>. Y para entender la conexión entre estrés oxidativo e inflamación, lee también <Link to="/blog/estres-oxidativo-explicado">esta guía</Link>.</p>
    </>
  ),
};

export default ARTICLE_CONTENT_4;
